import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Toaster } from 'sonner';

import { adoptLandingParamsFromSignInState } from '@core/analytics/landing-params.js';
import { AuthKitBridge } from '@core/auth/authkit-bridge.js';
import { DevAuthProvider } from '@core/auth/dev-auth-provider.js';
import { readDevAuthToken } from '@core/auth/dev-auth.js';
import { setAccessTokenGetter } from '@core/auth/get-access-token.js';
import { useAuth } from '@core/auth/use-auth.js';
import { queryClient } from '@core/query/client.js';
import { clientEnv } from '@core/types/env.js';

import { AppErrorBoundary } from './error-boundary.js';

/**
 * Bridges the AuthKit `getAccessToken` function into our module-level getter
 * so the API clients in `@core` can reach it without depending on React.
 * Mounts once inside <AuthKitProvider> at app boot.
 *
 * The getter is registered exactly once and always delegates to the *latest*
 * AuthKit `getAccessToken` via a ref — it never closes over the value from the
 * render it was registered in. This avoids a stale-closure race on the commit
 * where AuthKit swaps its placeholder client for the authenticated one
 * (`isLoading` → false): that same commit mounts the authenticated tree, whose
 * first `/me` query dispatches from a passive effect. Passive effects run
 * child-before-parent, so refreshing the getter in a passive effect here would
 * land *after* that query had already read a stale getter — the request would
 * reject before `fetch` and TanStack Query's 1s retry backoff would delay the
 * real `/me` by a full second. The ref is refreshed in a *layout* effect, which
 * runs before any passive effect, so it is current before the query dispatches.
 * See `docs/features/platform-foundation/chronicle.md` (2026-06-04).
 */
function AccessTokenBridge({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const getAccessTokenRef = useRef(getAccessToken);
  useLayoutEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  useEffect(() => {
    setAccessTokenGetter(async (options) => {
      const token = await getAccessTokenRef.current(options);
      return token ?? null;
    });
  }, []);
  return <>{children}</>;
}

export type AppProvidersProps = {
  children: ReactNode;
  /** Test-only override so unit tests can swap a fake QueryClient. */
  queryClientOverride?: QueryClient;
};

// Refresh the access token 60s before its `exp`. SDK default is 10s, which
// leaves a tight race against backend `jose` JWT verification (default
// clockTolerance=0) and slow requests. See platform-foundation/design.md §8
// "SPA session lifecycle" for the full rationale.
//
// `onRefreshFailure` is intentionally not configured: SDK signOut() does not
// transition internal state away from AUTHENTICATED, so an in-flight query's
// force-refresh during the window between memoryStorage being cleared and
// the WorkOS-logout navigation completing would fire the callback and race
// any signIn() redirect against the legitimate logout. Without it: refresh
// fails → SDK state=ERROR → next getAccessToken throws LoginRequiredError,
// which `isSessionInvalidError` routes to the same session-invalidated
// panel as a backend 401 (the provider keeps `user` populated after a
// failed refresh, so RequireAuth alone would not redirect).
const REFRESH_BUFFER_SECONDS = 60;

// Force devMode on in every environment so the SDK persists the refresh
// token to localStorage. The SDK's default (devMode auto-true only on
// `localhost`/`127.0.0.1`) otherwise keeps the refresh token in
// memoryStorage on staging/production and bootstraps refresh from a
// cookie on `api.workos.com` — a third-party cookie from the SPA's
// origin, blocked by Safari ITP, Brave, and Chrome's third-party cookie
// blocking, so every page reload drops the session. WorkOS's only
// supported fix is a paid custom Authentication API domain via
// `apiHostname`; until that's provisioned we accept the trade-off of
// the refresh token being JS-reachable. See platform-foundation/design.md
// §8 "SPA session lifecycle" and chronicle 2026-05-19.
const AUTHKIT_DEV_MODE = true;

export function AppProviders({ children, queryClientOverride }: AppProvidersProps) {
  // `eco-platform.dev-auth-token` is the integration-suite bypass: Playwright's
  // `signInAs(page)` (frontend/playwright/helpers/fake-auth.ts) plants a stub
  // token + mocks `/api/v1/me`, then DevAuthProvider hands the same stub to
  // every API client. Production bundles never reach this branch
  // (`readDevAuthToken` is gated by `import.meta.env.DEV`). The visible
  // DevSignInButton on /login does NOT use this path — it plants a real
  // WorkOS refresh token into AuthKit's own storage slot so the SDK's
  // refresh loop handles the dev session like any other.
  const devToken = readDevAuthToken();

  if (devToken !== null) {
    return (
      <QueryClientProvider client={queryClientOverride ?? queryClient}>
        <DevAuthProvider token={devToken}>
          <AccessTokenBridge>
            <AppErrorBoundary>{children}</AppErrorBoundary>
          </AccessTokenBridge>
          <Toaster richColors position="top-right" />
        </DevAuthProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClientOverride ?? queryClient}>
      <AuthKitProvider
        clientId={clientEnv.VITE_WORKOS_CLIENT_ID}
        redirectUri={
          clientEnv.VITE_WORKOS_REDIRECT_URI ?? `${window.location.origin}/auth/callback`
        }
        refreshBufferInterval={REFRESH_BUFFER_SECONDS}
        devMode={AUTHKIT_DEV_MODE}
        // The landing-page attribution (`utm_*` / `ref`) rides through the
        // WorkOS round-trip as OAuth `state`, sent by SignInRedirect; on the
        // callback page it is put back so the consent-gated analytics init
        // still sees it. See core/analytics/landing-params.ts.
        onRedirectCallback={({ state }) => adoptLandingParamsFromSignInState(state)}
      >
        <AuthKitBridge>
          <AccessTokenBridge>
            <AppErrorBoundary>{children}</AppErrorBoundary>
          </AccessTokenBridge>
          <Toaster richColors position="top-right" />
        </AuthKitBridge>
      </AuthKitProvider>
    </QueryClientProvider>
  );
}
